import { useEffect, useState } from 'react';
import api from '../api';
import { isIos, isInstalled, canInstall, install, onPhoneChange, pushBlockedWhy, currentSubscription, turnOnPush, turnOffPush } from './phone';

// Getting MouldOS onto this phone: on the home screen, and notifications on.
// In the recorder's list it says only what is still to do, and can be put away;
// on Account it is always there.

const TEAL = '#14b8a6', AMBER = '#f59e0b', GREEN = '#34d399', DIM = '#9ca3af', LINE = '#2b333e', CARD = '#1a2028', INK = '#e5e7eb';
const HIDE_KEY = 'mouldos.phoneSetup.hidden';

export default function PhoneSetup({ compact = false }) {
  const [, redraw] = useState(0);
  const [on, setOn] = useState(null);       // notifications on this phone
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(HIDE_KEY) === '1'; } catch { return false; } });

  useEffect(() => onPhoneChange(() => redraw(n => n + 1)), []);
  useEffect(() => { currentSubscription().then(s => setOn(!!s)).catch(() => setOn(false)); }, []);

  const installed = isInstalled();
  const blocked = pushBlockedWhy();
  const installDone = installed;
  const pushDone = on === true;
  if (compact && (hidden || (installDone && pushDone) || on === null)) return null;

  async function push(fn) {
    setBusy(true); setError(''); setSent(null);
    try { await fn(); setOn(!!(await currentSubscription())); }
    catch (err) { setError(err.response?.data?.error || err.message || 'That did not work. Try again.'); }
    setBusy(false);
  }
  async function test() {
    setBusy(true); setError('');
    try { const { data } = await api.post('/api/v1/push/test'); setSent(data.sent > 0 ? 'Sent: it should show on this phone in a moment.' : 'Nothing was sent: turn notifications off and on again.'); }
    catch (err) { setError(err.response?.data?.error || 'That did not work. Try again.'); }
    setBusy(false);
  }

  return (
    <div style={{ margin: compact ? '18px 12px 6px' : 0, padding: '12px 13px', background: CARD, border: `1px solid ${LINE}`, borderRadius: 12, color: INK, fontSize: 13.5, lineHeight: 1.5, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>On this phone</div>

      {/* The home screen */}
      {installDone ? (
        <div style={{ color: GREEN, fontSize: 13 }}>✓ On the home screen</div>
      ) : canInstall() ? (
        <div>
          <div style={{ color: DIM, fontSize: 13 }}>Opens like an app, even with no signal.</div>
          <button disabled={busy} onClick={() => install()} style={{ ...btn, background: TEAL, borderColor: TEAL, color: '#0b1215', marginTop: 6 }}>Add MouldOS to this phone</button>
        </div>
      ) : isIos() ? (
        <div style={{ color: DIM, fontSize: 13 }}>
          <b style={{ color: INK }}>Add it to the home screen</b> so it opens like an app, even with no signal: in Safari tap <b style={{ color: INK }}>Share</b> <i className="fa-solid fa-arrow-up-from-bracket" aria-hidden="true" />, then <b style={{ color: INK }}>Add to Home Screen</b>. Then open it from that icon and sign in once more: the icon keeps its own sign-in.
        </div>
      ) : (
        <div style={{ color: DIM, fontSize: 13 }}>Your browser's menu has <b style={{ color: INK }}>Install app</b> or <b style={{ color: INK }}>Add to Home screen</b>, so it opens like an app, even with no signal.</div>
      )}

      {/* Notifications */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${LINE}` }}>
        {pushDone ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span style={{ color: GREEN, fontSize: 13 }}>✓ Notifications on</span>
            <button disabled={busy} onClick={test} style={btn}>Send a test</button>
            <button disabled={busy} onClick={() => push(turnOffPush)} style={{ ...btn, color: DIM }}>Turn off</button>
          </div>
        ) : blocked === 'ios-install' ? (
          <div style={{ color: DIM, fontSize: 13 }}>On an iPhone, notifications work once MouldOS is on the home screen (iOS 16.4 or later). Add it first, then come back here from the icon.</div>
        ) : blocked === 'denied' ? (
          <div style={{ color: AMBER, fontSize: 13 }}>This phone said no to notifications. To change that: its settings, then notifications for MouldOS or this site. The Today card and the Record button still show everything.</div>
        ) : blocked === 'unsupported' ? (
          <div style={{ color: DIM, fontSize: 13 }}>This browser cannot show notifications. The Today card and the Record button still show everything.</div>
        ) : (
          <div>
            <div style={{ color: DIM, fontSize: 13 }}>A nudge when a measurement is given to you, and if a recording is left running.</div>
            <button disabled={busy} onClick={() => push(turnOnPush)} style={{ ...btn, background: TEAL, borderColor: TEAL, color: '#0b1215', marginTop: 6 }}>Turn on notifications</button>
          </div>
        )}
        {sent && <div style={{ color: DIM, fontSize: 12.5, marginTop: 6 }}>{sent}</div>}
        {error && <div style={{ color: AMBER, fontSize: 12.5, marginTop: 6 }}>{error}</div>}
      </div>

      {compact && (
        <button onClick={() => { setHidden(true); try { localStorage.setItem(HIDE_KEY, '1'); } catch { /* shown again next time */ } }}
          style={{ background: 'none', border: 'none', color: DIM, fontSize: 12, padding: '10px 0 0', cursor: 'pointer', textDecoration: 'underline' }}>
          Not now. It stays on your Account page.
        </button>
      )}
    </div>
  );
}

const btn = { background: 'none', border: `1px solid ${LINE}`, borderRadius: 8, color: INK, padding: '7px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 13 };
